// File: src/components/OrientationPrompt.jsx
import React, { useEffect, useRef, useState } from 'react';
import { FaSyncAlt } from 'react-icons/fa';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

const isTrueMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|tablet|mobile/i.test(userAgent);
};

export default function OrientationPrompt() {
  const [isLandscape, setIsLandscape] = useState(false);
  const scrollPositionRef = useRef(0);
  const wasLandscapeRef = useRef(false);

  useEffect(() => {
    if (!isTrueMobileDevice()) return;

    const lockBody = () => {
      scrollPositionRef.current = window.scrollY || window.pageYOffset || 0;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.touchAction = 'none';
      document.body.style.overflow = 'hidden';
    };

    const unlockBody = () => {
      const y = scrollPositionRef.current || 0;

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.touchAction = '';
      document.body.style.overflow = '';

      requestAnimationFrame(() => {
        window.scrollTo(0, y);
      });
    };

    const handleOrientation = () => {
      const landscape = window.matchMedia('(orientation: landscape)').matches;

      setIsLandscape(landscape);

      // Entering landscape
      if (landscape && !wasLandscapeRef.current) {
        wasLandscapeRef.current = true;
        lockBody();
        return;
      }

      // Leaving landscape
      if (!landscape && wasLandscapeRef.current) {
        wasLandscapeRef.current = false;
        unlockBody();
        return;
      }

      // Important:
      // Do nothing on normal mobile Safari resize while already portrait.
      // Safari fires resize while scrolling because the address bar changes height.
    };

    window.addEventListener('orientationchange', handleOrientation);
    window.addEventListener('resize', handleOrientation);

    handleOrientation();

    return () => {
      window.removeEventListener('orientationchange', handleOrientation);
      window.removeEventListener('resize', handleOrientation);

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      document.body.style.touchAction = '';
      document.body.style.overflow = '';
    };
  }, []);

  if (!isLandscape || !isTrueMobileDevice()) return null;

  return (
    <>
      <div style={overlayStyle}>
        <div className="orientationPromptCard" style={cardStyle}>
          <div style={logoWrapStyle}>
            <TribeLogo className="orientationTribeLogo" style={logoStyle} />
          </div>

          <div className="phoneContainerResponsive" style={phoneContainerStyle}>
            <div className="phoneMockResponsive" style={phoneMockStyle}>
              <div style={notchStyle} />

              <div style={screenStyle}>
                <div style={rotateCircleStyle}>
                  <FaSyncAlt style={rotateIconStyle} />
                </div>
              </div>
            </div>
          </div>

          <div style={copyWrapStyle}>
            <h1 style={headingStyle}>Rotate your device</h1>

            <p style={subtitleStyle}>
              We optimize for portrait mode to give you the best experience.
              Turn your phone upright to continue.
            </p>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes orientationFadeIn {
            from {
              opacity: 0;
            }

            to {
              opacity: 1;
            }
          }

          @keyframes phoneRotate {
            0% {
              transform: rotate(90deg);
              opacity: 0;
            }

            10% {
              opacity: 1;
            }

            22% {
              transform: rotate(90deg);
            }

            36% {
              transform: rotate(0deg);
            }

            78% {
              transform: rotate(0deg);
            }

            90% {
              opacity: 1;
            }

            100% {
              transform: rotate(90deg);
              opacity: 0;
            }
          }

          .orientationTribeLogo :is(path,circle,rect,polygon,polyline,ellipse) {
            fill: #050505 !important;
          }

          .orientationTribeLogo [fill="none"] {
            fill: none !important;
          }

          .orientationTribeLogo * {
            stroke: none !important;
          }

          @media (max-height: 600px) {
            .orientationPromptCard {
              gap: 12px !important;
              padding: 16px 18px !important;
            }

            .phoneContainerResponsive {
              width: 104px !important;
              height: 128px !important;
            }

            .phoneMockResponsive {
              width: 56px !important;
              height: 112px !important;
              border-radius: 14px !important;
            }
          }
        `}
      </style>
    </>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 99999,
  background: 'rgba(255,255,255,0.96)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '14px 16px 16px',
  boxSizing: 'border-box',
  overflowY: 'auto',
  animation: 'orientationFadeIn 180ms ease-out',
};

const cardStyle = {
  width: 'min(92vw, 340px)',
  minHeight: 'auto',
  padding: '10px 22px 18px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 10,
  textAlign: 'center',
  fontFamily:
    "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};
const logoWrapStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const logoStyle = {
  width: 82,
  height: 'auto',
  display: 'block',
};

const phoneContainerStyle = {
  width: 112,
  height: 126,
  perspective: '1000px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const phoneMockStyle = {
  width: 56,
  height: 112,
  borderRadius: 17,
  background: '#fff',
  border: '2.5px solid #111',
  position: 'relative',
  boxShadow: '0 14px 34px rgba(0,0,0,0.12)',
  transformStyle: 'preserve-3d',
  animation: 'phoneRotate 3.8s cubic-bezier(0.65, 0.05, 0.36, 1) infinite',
};

const notchStyle = {
  position: 'absolute',
  top: 7,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 28,
  height: 5,
  borderRadius: 999,
  background: '#111',
  zIndex: 2,
};

const screenStyle = {
  position: 'absolute',
  inset: 7,
  borderRadius: 13,
  background:
    'linear-gradient(180deg, rgba(247,247,248,1) 0%, rgba(255,255,255,1) 100%)',
  border: '1px solid rgba(17,17,17,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const rotateCircleStyle = {
  width: 34,
  height: 34,
  borderRadius: 999,
  background: '#111',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const rotateIconStyle = {
  color: '#fff',
  fontSize: 13,
};

const copyWrapStyle = {
  maxWidth: 270,
};

const headingStyle = {
  margin: 0,
  color: '#111',
  fontSize: '1.15rem',
  lineHeight: 1.05,
  fontWeight: 850,
  letterSpacing: '-0.035em',
};

const subtitleStyle = {
  margin: '8px 0 0',
  color: 'rgba(17,17,17,0.58)',
  fontSize: '0.9rem',
  lineHeight: 1.36,
  fontWeight: 500,
  letterSpacing: '-0.018em',
};